/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  PlusCircle,
  Building2,
  Layers
} from 'lucide-react';
import { subAccountService } from '../services/subAccountService';
import { accountService } from '../services/accountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { useAuth } from '../context/AuthContext';
import toastService from '../utils/toastService';
import BrandingSection from '../components/BrandingSection';
import { useAccountBranding } from '../hooks/useAccountBranding';
import MultiSelect from '../components/MultiSelect';
import { FIELD_LIMITS } from '../utils/constants';
import { validateTextField } from '../utils/validation';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const ROUTE_SUB_ACCOUNTS = '/sub-accounts';

const NewSubAccount = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [form, setForm] = useState({
    account_id: '',
    name: '',
    slug: '',
    status: 'active',
    externalSubAccountId: '',
    theme: '', // empty = use the parent account's theme
    logo_url: '',
    favicon_url: '',
    serviceRequestTypeIds: []
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [availableServiceRequestTypes, setAvailableServiceRequestTypes] = useState([]);
  // Parent account branding — shown as what empty branding fields fall back to
  const accountBranding = useAccountBranding(form.account_id);

  // Stable upload function — shared by brand logo and workflow logo fields
  const handleLogoUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file);
    return res.data; // { url: string }
  }, []);

  // Stable upload function for favicon
  const handleFaviconUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file, null, 'favicon');
    return res.data; // { url: string }
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (user?.scope === 'account') {
        // For account scope users, auto-set their accountUID
        setForm(f => ({ ...f, account_id: user.accountUID }));
        return;
      }

      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setParentAccounts(data.data || data || []);
      } catch (err) {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [user]);

  useEffect(() => {
    const loadTypesForAccount = async () => {
      if (!form.account_id) {
        setAvailableServiceRequestTypes([]);
        setForm(f => (f.serviceRequestTypeIds.length ? { ...f, serviceRequestTypeIds: [] } : f));
        return;
      }
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ accountUid: form.account_id, limit: 100 });
        const types = data.data || [];
        setAvailableServiceRequestTypes(types);
        const availableIds = new Set(types.map(t => t.uid));
        setForm(f => ({ ...f, serviceRequestTypeIds: f.serviceRequestTypeIds.filter(id => availableIds.has(id)) }));
      } catch (err) {
        setAvailableServiceRequestTypes([]);
      }
    };
    loadTypesForAccount();
  }, [form.account_id]);

  const validateForm = () => {
    const errors = {};
    if (!form.account_id) {
      errors.account_id = `Parent ${ACCOUNT_LABEL.singularLower} is required`;
    } else if (user?.scope === 'platform') {
      const selectedAccount = parentAccounts.find(acc => acc.id === form.account_id);
      if (selectedAccount?.status === 'inactive') {
        errors.account_id = `Cannot create ${SUB_ACCOUNT_LABEL.singularLower} under an inactive ${ACCOUNT_LABEL.singularLower}`;
      }
    }
    if (!form.name.trim()) {
      errors.name = `${SUB_ACCOUNT_LABEL.singular} name is required`;
    }
    const nameError = validateTextField(form.name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT });
    if (nameError) {
      errors.name = nameError;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldBlur = useCallback((fieldKey, value, fieldLabel, options) => {
    const errorMsg = validateTextField(value, fieldLabel, options);
    setFieldErrors(prev => ({ ...prev, [fieldKey]: errorMsg }));
  }, []);

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setForm(f => ({ ...f, name, slug }));
    if (fieldErrors.name) {
      setFieldErrors(prev => ({ ...prev, name: validateTextField(name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT }) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);

      const { data } = await subAccountService.createSubAccount(form.account_id, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        status: form.status,
        externalSubAccountId: form.externalSubAccountId.trim() || undefined,
        theme: form.theme,
        logoUrl: form.logo_url,
        faviconUrl: form.favicon_url,
        showQrCode: false,
        serviceRequestTypeIds: form.serviceRequestTypeIds
      });
      if (user?.subAccountUID && user.subAccountUID === data?.id) {
        updateProfile({
          subAccountLogoUrl: form.logo_url,
          subAccountTheme: form.theme,
          subAccountShowQrCode: false
        });
      }
      toastService.success(`${SUB_ACCOUNT_LABEL.singular} created successfully`);
      navigate(ROUTE_SUB_ACCOUNTS);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || `Failed to create ${SUB_ACCOUNT_LABEL.singularLower}`);
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
      fieldErrors[field] ? 'border-error ring-1 ring-error/20' : 'border-base-300'
    }`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${
      fieldErrors[field] ? 'border-error ring-1 ring-error/20' : 'border-base-300'
    }`;

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SUB_ACCOUNT_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                New Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Create {SUB_ACCOUNT_LABEL.singular}
            </h1>
            <p className="text-xs text-base-content/50">
              Provide {SUB_ACCOUNT_LABEL.singularLower} details, assign {SERVICE_REQUEST_LABEL.singularLower} types, and customize branding.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization & Scope Selection (Platform Scope Only) */}
        {user?.scope === 'platform' && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Organization & Scope
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="parentAccount" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  Parent {ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="parentAccount"
                  className={selectClass('account_id')}
                  value={form.account_id}
                  onChange={(e) => {
                    setForm(f => ({ ...f, account_id: e.target.value }));
                    if (fieldErrors.account_id) setFieldErrors(prev => ({ ...prev, account_id: null }));
                  }}
                >
                  <option value="">— Select Parent {ACCOUNT_LABEL.singular} —</option>
                  {parentAccounts.map((acc) => (
                    <option
                      key={acc.id}
                      value={acc.id}
                      disabled={acc.status === 'inactive'}
                    >
                      {acc.name}{acc.status === 'inactive' ? ' (Inactive)' : ''}
                    </option>
                  ))}
                </select>
                {fieldErrors.account_id && (
                  <p className="text-error text-xs font-medium">{fieldErrors.account_id}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sub-Account Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Layers size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {SUB_ACCOUNT_LABEL.singular} Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Sub-Account Name */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                {SUB_ACCOUNT_LABEL.singular} Name *
              </label>
              <input
                id="name"
                type="text"
                maxLength={FIELD_LIMITS.DEFAULT_TEXT}
                placeholder="e.g. Downtown Service Center"
                className={inputClass('name')}
                value={form.name}
                onChange={handleNameChange}
                onBlur={() => handleFieldBlur('name', form.name, `${SUB_ACCOUNT_LABEL.singular} name`, { minLength: FIELD_LIMITS.MIN_LENGTH, maxLength: FIELD_LIMITS.DEFAULT_TEXT })}
              />
              {fieldErrors.name && (
                <p className="text-error text-xs font-medium">{fieldErrors.name}</p>
              )}
            </div>

            {/* External Sub-Account ID */}
            <div className="space-y-1.5">
              <label htmlFor="externalSubAccountId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                External {SUB_ACCOUNT_LABEL.singular} ID
              </label>
              <input
                id="externalSubAccountId"
                type="text"
                maxLength={255}
                placeholder="Optional"
                className={inputClass('externalSubAccountId')}
                value={form.externalSubAccountId}
                onChange={e => setForm(f => ({ ...f, externalSubAccountId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Service Request Types */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4 relative z-20">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {SERVICE_REQUEST_LABEL.singular} Types
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <MultiSelect
              label={`Assigned ${SERVICE_REQUEST_LABEL.singular} Types`}
              searchPlaceholder={`Search ${SERVICE_REQUEST_LABEL.singularLower} types...`}
              placeholder={form.account_id ? `Select ${SERVICE_REQUEST_LABEL.singularLower} types (optional)` : `Select a parent ${ACCOUNT_LABEL.singularLower} first`}
              options={availableServiceRequestTypes.map(t => ({ id: t.uid, key: t.name }))}
              selected={form.serviceRequestTypeIds}
              onChange={(ids) => setForm(f => ({ ...f, serviceRequestTypeIds: ids }))}
            />
          </div>
        </div>

        {/* Sub-Account Branding Section — empty fields fall back to the parent account */}
        <BrandingSection
          title={`${SUB_ACCOUNT_LABEL.singular} Branding`}
          value={form}
          onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
          uploadLogo={handleLogoUpload}
          uploadFavicon={handleFaviconUpload}
          radioName="sub-account-theme"
          inherited={{ source: accountBranding ? `${ACCOUNT_LABEL.singularLower} branding` : 'platform default', ...accountBranding }}
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2 relative z-0">
          <button
            type="button"
            onClick={() => navigate(ROUTE_SUB_ACCOUNTS)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating {SUB_ACCOUNT_LABEL.singular}...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create {SUB_ACCOUNT_LABEL.singular}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewSubAccount;
